<template>
  <div class="subscription-pdp-add-to-cart">
    <div>
      <SloField class="w-100">
        <div class="flex flex-row items-center gap-sm">
          <SloLabel class="w-100">
            <div class="flex items-center gap-sm w-100">
              <input
                type="radio"
                id="add-to-cart-mode--one-time-purchase"
                value="oneTimePurchase"
                @click="handlePurchaseModeChange('oneTimePurchase')"
                v-model="purchaseMode"
                :checked="purchaseMode === 'oneTimePurchase'"
                class="m-0"
              />
              {{ 'One-time Purchase' | t }}
            </div>
            <div v-if="purchaseMode === 'oneTimePurchase'" class="flex items-center w-100">
              <span v-if="discontinued" class="label label-danger discontinued p-sm">
                {{ 'Discontinued' | t }}
              </span>
              <div v-else class="flex flex-col items-stretch justify-start w-100">
                <div
                  v-if="product?.estimatedShipDate"
                  class="c-green-400 fw-strong text-sm mt-md mb-md"
                >
                  {{ 'Estimated ship date' | t }}: {{ product?.estimatedShipDate }}
                </div>
                <div>
                  <p class="text-lg fw-strong">{{ product?.price?.formattedValue }} {{ product?.uom }}</p>
                </div>
                <div
                  class="flex flex-col justify-between items-center gap-md sm:flex-row mt-md"
                  style="max-width: 140px;"
                >
                  <SloQuantityInput
                    :quantity="selectedQuantity"
                    :min="product?.minOrderQuantity"
                    @updated="handleQuantityUpdate($event)"
                  />
                  <div>
                    <SloButton @click="handleAddToCart" class="add-to-cart--button">
                      {{ cartEntry ? 'Update Quantity' : 'Add to Cart' | t }}
                    </SloButton>
                  </div>
                </div>
                <SloMinOrderQuantityWarning :min-order-quantity="product?.minOrderQuantity" />
              </div>
            </div>
          </SloLabel>
        </div>
      </SloField>
    </div>
    <div>
      <SloField class="w-100">
        <div class="flex flex-row items-center gap-sm">
          <SloLabel class="w-100">
            <div class="flex items-center gap-sm">
              <input
                type="radio"
                id="add-to-cart-mode--subscription"
                value="subscription"
                @click="handlePurchaseModeChange('subscription')"
                v-model="purchaseMode"
                :checked="purchaseMode === 'subscription'"
                class="m-0"
              />
              {{ 'Subscribe & Save' | t }}
            </div>
            <div class="flex items-center w-100 mt-md">
              <span v-if="discontinued" class="label label-danger discontinued p-sm">
                {{ 'Discontinued' | t }}
              </span>
              <div v-else class="flex flex-col items-start justify-start gap-md w-100">
                <div
                  v-if="purchaseMode === 'subscription' && product?.estimatedShipDate"
                  class="c-green-400 fw-strong text-sm"
                >
                  {{ 'Estimated ship date' | t }}: {{ product?.estimatedShipDate }}
                </div>
                <div class="flex items-center justify-between flex-wrap gap-md w-100">
                  <div class="flex items-center gap-xs">
                    <p class="m-0 text-lg fw-book">
                      <span class="text-lg fw-strong">{{ product?.subscriptionPrice?.formattedValue }}</span>
                      {{ product?.uom }}
                    </p>
                    <span class="inline-block ml-sm c-green-400 text-lg fw-strong">
                      {{ product?.subscriptionPercentageDiscount }}% {{ 'savings' | t }}
                    </span>
                  </div>
                  <p v-if="purchaseMode === 'oneTimePurchase'" class="fw-book m-0">{{ 'Subscription pricing is subject to change at anytime.' | t }}</p>
                </div>
                <div v-if="purchaseMode === 'subscription'">
                  <p class="fw-book">{{ 'Save ' | t }} {{ product?.subscriptionPercentageDiscount }}% {{ 'now on repeat deliveries. No fees. Cancel anytime.' | t  }}</p>
                </div>
                <div v-if="purchaseMode === 'subscription'">
                  <div class="add-subscription flex flex-col justify-between items-center gap-md sm:flex-row">
                    <SloQuantityInput
                      :quantity="selectedQuantity"
                      :min="product?.minOrderQuantity"
                      @updated="handleQuantityUpdate($event)"
                    />
                    <SloField class="add-subscription--frequency m-0">
                      <SloSelect
                        v-model="selectedSubscriptionFrequency"
                        :placeholder="'Select a subscription frequency' | t"
                        name="subscription_frequency"
                        class="m-0"
                      >
                        <option
                          v-for="(subscriptionFrequency, index) in product?.subscriptionFrequencies"
                          :key="`subscription-frequency-${index}`"
                          v-html="subscriptionFrequency.name"
                          :value="subscriptionFrequency.code"
                        />
                      </SloSelect>
                    </SloField>
                    <div>
                      <SloButton @click="handleAddToCart" class="add-subscription--button">
                        {{ cartEntry?.subscriptionFrequency ? 'Update Subscription' : 'Add to Cart' | t }}
                      </SloButton>
                    </div>
                  </div>
                  <SloMinOrderQuantityWarning :min-order-quantity="product?.minOrderQuantity" />
                </div>
              </div>
            </div>
          </SloLabel>
        </div>
      </SloField>
    </div>
    <transition name="fade-up">
      <SloModal
        v-if="purchaseModeChangeConfirmationModal && authenticated"
        :maxWidth="500"
        :form="false"
        @dismiss="handleCancelPurchaseModeChangeConfirmationModal"
      >
        <template v-slot:title>
          {{ 'Attention' | t }}
        </template>
        <div class="px-sm py-md bg-gray-50 rounded-md">
          <p class="m-0">{{ purchaseModeChangeConfirmationReason | t }}</p>
        </div>
        <template v-slot:footer>
          <div class="grid grid-cols-1 gap-md w-100 sm:grid-cols-2">
            <SloButton
              @click="handleCancelPurchaseModeChangeConfirmationModal"
              :disabled="purchaseModeChangeLoading"
              fill="outline"
            >
              {{ 'Discard Changes' | t }}
            </SloButton>
            <SloButton
              @click="handleConfirmPurchaseModeChange"
              :disabled="purchaseModeChangeLoading"
            >
              <SloLoader :loading="purchaseModeChangeLoading" size="md" color="white">
                {{ 'Confirm Changes' | t }}
              </SloLoader>
            </SloButton>
          </div>
        </template>
      </SloModal>
      <SubscriptionUnauthorizedModal
        v-else-if="shouldShowSubscriptionUnauthorizedModal"
        :product="product"
        @dismiss="handleCancelPurchaseModeChangeConfirmationModal"
      />
    </transition>
  </div>
</template>

<script>
import {mapActions, mapGetters} from 'vuex';
import t from '../../main/i18n';
import {findCartEntry, handleApiErrors, tagManagerTrack} from '../../main/utils';
import SloAddToCart from '../../../lib/components/SloAddToCart.vue';
import SloProductMeta from '../../../lib/components/SloProductMeta.vue';
import SloInput from '../../../lib/components/SloInput.vue';
import SloField from '../../../lib/components/SloField.vue';
import SloLabel from '../../../lib/components/SloLabel.vue';
import SloSelect from '../../../lib/components/SloSelect.vue';
import SloNumberInput from '../../../lib/components/SloNumberInput.vue';
import SloButton from '../../../lib/components/SloButton.vue';
import SloModal from '../../../lib/components/SloModal.vue';
import SloLoader from '../../../lib/components/SloLoader.vue';
import SloQuantityInput from '../../../lib/components/SloQuantityInput.vue';
import SubscriptionUnauthorizedModal from './SubscriptionUnauthorizedModal.vue';
import SloMinOrderQuantityWarning from '../../../lib/components/SloMinOrderQuantityWarning.vue';

export default {
  name: 'SubscriptionPDPAddToCart',
  components: {
    SloLoader,
    SloModal,
    SloButton,
    SloNumberInput,
    SloSelect,
    SloLabel,
    SloField,
    SloInput,
    SloProductMeta,
    SloAddToCart,
    SloQuantityInput,
    SloMinOrderQuantityWarning,
    SubscriptionUnauthorizedModal
  },
  filters: {
    t
  },
  props: {
    product: {type: Object, required: true},
    discontinued: {type: Boolean, default: false}
  },
  created() {
    this.purchaseMode = this.cartEntry?.subscriptionFrequency ? 'subscription' : 'oneTimePurchase';
    this.selectedQuantity = this.cartEntry?.quantity ?? this.product?.minOrderQuantity;
    this.selectedSubscriptionFrequency = this.cartEntry?.subscriptionFrequency ?? this.product?.subscriptionFrequencies?.[0]?.code;
  },
  data() {
    return {
      purchaseMode: 'oneTimePurchase',
      previousPurchaseMode: null,
      selectedQuantity: 1,
      selectedSubscriptionFrequency: null,
      purchaseModeChangeLoading: false,
      purchaseModeChangeConfirmationModal: false,
      purchaseModeChangeConfirmationReason: null
    };
  },
  computed: {
    ...mapGetters('auth', ['authenticated']),
    ...mapGetters('marketplace', ['cartEntries']),
    sku() {
      return this.product?.code;
    },
    cartEntry() {
      const cartEntry = findCartEntry(this.cartEntries, this.sku);
      if (cartEntry) {
        this.selectedQuantity = cartEntry.quantity;
        this.selectedSubscriptionFrequency =
          cartEntry.subscriptionFrequency ?? this.product?.subscriptionFrequencies?.[0]?.code;
        return cartEntry;
      }

      this.selectedQuantity = this.product?.minOrderQuantity ?? 1;
      this.selectedSubscriptionFrequency = this.product?.subscriptionFrequencies?.[0]?.code;

      return false;
    },
    shouldShowSubscriptionUnauthorizedModal() {
      return !this.authenticated
        && (this.purchaseModeChangeConfirmationModal || this.purchaseMode === 'subscription');
    }
  },
  methods: {
    ...mapActions('marketplace', ['addToCart', 'removeCartEntry']),
    async handleAddToCart() {
      const data = {
        sku: this.sku,
        quantity: this.selectedQuantity,
        frequency: this.purchaseMode === 'subscription' ? this.selectedSubscriptionFrequency : null,
        entryNumber: this.cartEntry?.entryNumber ?? false
      };

      await this.addToCart(data)
        .then(() => {
          const cartEntry = findCartEntry(this.cartEntries, this.sku);
          if (cartEntry) {
            tagManagerTrack('add_to_cart', {
              currency: this.cartResponse?.subTotal?.currencyIso,
              user: this.user?.accountNumber || 'guest',
              cartId: this.cartId,
              cartSubTotal: this.cartResponse?.subTotal?.value,
              subscription: true,
              items: [
                {
                  item_id: this.sku,
                  item_name: cartEntry?.product?.number,
                  item_category: cartEntry?.product?.type,
                  item_list_name: this.listName,
                  price: cartEntry?.product?.price?.value,
                  quantity: this.quantity,
                  totalPrice: cartEntry?.totalPrice?.value
                }
              ]
            });
          }
        })
        .catch(e => handleApiErrors(e, this));
    },
    handleQuantityUpdate(value) {
      this.selectedQuantity = value;
    },
    handlePurchaseModeChange(value) {
      if (this.cartEntry?.subscriptionFrequency && value === 'oneTimePurchase') {
        this.previousPurchaseMode = 'subscription';
        this.purchaseModeChangeConfirmationModal = true;
        this.purchaseModeChangeConfirmationReason = 'Replace this Subscribe & Save item with a One-time purchase?';
      }

      if (this.cartEntry && !this.cartEntry?.subscriptionFrequency && value === 'subscription') {
        this.previousPurchaseMode = 'oneTimePurchase';
        this.purchaseModeChangeConfirmationModal = true;
        this.purchaseModeChangeConfirmationReason = 'This item has already been added to the cart. Adding this item with Subscribe & Save will replace the existing item in the cart.';
      }
    },
    async handleConfirmPurchaseModeChange() {
      this.purchaseModeChangeLoading = true;
      const cartEntry = findCartEntry(this.cartEntries, this.sku);
      await this.removeCartEntry({entryNumber: cartEntry?.entryNumber});
      this.purchaseModeChangeLoading = false;
      this.purchaseModeChangeConfirmationModal = false;
      this.purchaseModeChangeConfirmationReason = null;
    },
    handleCancelPurchaseModeChangeConfirmationModal() {
      if (!this.authenticated) this.purchaseMode = 'oneTimePurchase';
      this.purchaseModeChangeLoading = false;
      this.purchaseModeChangeConfirmationModal = false;
      this.purchaseModeChangeConfirmationReason = null;
      if (this.previousPurchaseMode) this.purchaseMode = this.previousPurchaseMode;
    }
  }
};
</script>

<style lang="scss">
@import '../../../scss/system';

.subscription-pdp-add-to-cart {
  margin-top: 32px;
  border: 1px solid $color-gray-300;
  border-radius: 12px;

  > div {
    padding: 24px;

    &:first-child {
      border-bottom: 1px solid $color-gray-300;
    }
  }
}

.add-subscription {
  max-width: 220px;

  &--quantity {
    flex: 1 1 100%;
    @media (min-width: 767px) {
      flex: none;
    }
  }
  &--frequency {
    flex: 1 1 100%;
    @media (min-width: 767px) {
      flex: 0 0 275px;
    }
  }

  &--button {
    flex: 1 1 100%;
    @media (min-width: 767px) {
      flex: 0 0 416px;
    }
  }
}
</style>
